"use client";
import { useSnackbar } from "notistack";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { handleResetPin, handleForgot } from "../userControllers/pinController";

import authbg from "./assets/authbg.svg";
import blackload from "./assets/blackload.gif";
import logo from "./assets/logo.svg";

const ForgotPin: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [pin, setPin] = useState<string>("");
    const [pinConfirmation, setPinConfirmation] = useState<string>("");
    const [otp, setOtp] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [sendingOtp, setSendingOtp] = useState<boolean>(false);
    const [otpSent, setOtpSent] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const sendingOtpRef = useRef<boolean>(false);
    const initialOtpRequestedRef = useRef<boolean>(false);

    const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value.length <= 4) {
            setPin(value);
            setError("");
        }
    };

    const handlePinConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value.length <= 4) {
            setPinConfirmation(value);
            setError("");
        }
    };

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, "");
        if (value.length <= 6) {
            setOtp(value);
            setError("");
        }
    };

    const sendOtp = useCallback(async () => {
        if (sendingOtpRef.current) {
            return;
        }

        sendingOtpRef.current = true;
        setSendingOtp(true);
        try {
            await handleForgot();
            setOtpSent(true);
            enqueueSnackbar("OTP sent to your email. Please check your inbox.", {
                variant: "success",
            });
        } catch (err: unknown) {
            const errorMessage = (err as Error)?.message || "Unable to send OTP. Please try again in a moment.";
            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            sendingOtpRef.current = false;
            setSendingOtp(false);
        }
    }, [enqueueSnackbar]);

    const validateForm = (): boolean => {
        if (pin.length !== 4 || pinConfirmation.length !== 4 || otp.length !== 6) {
            setError("Please fill all fields correctly (4-digit PINs, 6-digit OTP).");
            return false;
        }
        if (pin !== pinConfirmation) {
            setError("PIN and PIN confirmation do not match.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setError("");
        try {
            await handleResetPin(
                {
                    pin,
                    pin_confirmation: pinConfirmation,
                    otp,
                },
                () => {
                    enqueueSnackbar("PIN reset successfully.", { variant: "success" });
                    navigate("/dashboard");
                },
                (error: unknown) => {
                    const errorMessage = (error as Error)?.message || "Failed to reset PIN. Please try again.";
                    enqueueSnackbar(errorMessage, { variant: "error" });
                    setError(errorMessage);
                }
            );
        } catch (error: unknown) {
            const errorMessage = (error as Error)?.message || "An unexpected error occurred. Please try again.";
            enqueueSnackbar(errorMessage, { variant: "error" });
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Auth check
    useEffect(() => {
        const storedItem = localStorage.getItem("saca_token");
        if (!storedItem) {
            navigate("/sign-in");
        }
    }, [navigate]);

    useEffect(() => {
        if (location.state?.autoSendOtp && !initialOtpRequestedRef.current) {
            initialOtpRequestedRef.current = true;
            sendOtp();
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate, sendOtp]);

    return (
        <div
            style={{
                backgroundImage: `url(${authbg})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
            }}
            className="w-full h-full bg-[#1212128d] z-[999] fixed top-0 left-0 p-6 flex justify-center items-center font-Manrope"
        >
            <div className="max-h-[550px] bg-[#FFFFFF] p-6 rounded-[15px] w-full max-w-xs md:max-w-[550px]">
                <div className="w-full h-full overflow-y-auto p-4 flex flex-col items-center">
                    <img src={logo} className="w-[20%] mb-6" alt="Logo" />
                    <div className="w-full flex flex-col items-center text-center text-xs text-[#475467] font-Manrope mb-4">
                        <p>
                            {otpSent
                                ? "Enter the OTP we emailed you, then set a new 4-digit PIN."
                                : "Tap the button below to receive a one-time OTP in your email. Enter it once it arrives."}
                        </p>
                        <button
                            type="button"
                            onClick={sendOtp}
                            disabled={sendingOtp}
                            className="mt-2 text-xs font-semibold text-[#317333] hover:text-[#255425] disabled:opacity-50"
                        >
                            {sendingOtp
                                ? "Sending OTP..."
                                : otpSent
                                    ? "Resend OTP"
                                    : "Send OTP"}
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
                        {/* New PIN */}
                        <div>
                            <label className="text-sm text-[#272D37] font-medium mb-2 block">
                                New PIN
                            </label>
                            <input
                                type="text"
                                value={pin}
                                onChange={handlePinChange}
                                className="w-full py-3 px-4 text-sm border-2 border-[#EAEBF0] rounded-[10px] bg-[#F1F1F1] focus:outline-none text-black focus:ring-2 focus:border-transparent"
                                placeholder="Enter 4-digit PIN"
                                maxLength={4}
                                required
                            />
                        </div>

                        {/* PIN Confirmation */}
                        <div>
                            <label className="text-sm text-[#272D37] font-medium mb-2 block">
                                Confirm PIN
                            </label>
                            <input
                                type="text"
                                value={pinConfirmation}
                                onChange={handlePinConfirmationChange}
                                className="w-full py-3 px-4 text-sm border-2 border-[#EAEBF0] rounded-[10px] bg-[#F1F1F1] focus:outline-none text-black focus:ring-2 focus:border-transparent"
                                placeholder="Confirm 4-digit PIN"
                                maxLength={4}
                                required
                            />
                        </div>

                        {/* OTP */}
                        <div>
                            <label className="text-sm text-[#272D37] font-medium mb-2 block">
                                OTP
                            </label>
                            <input
                                type="text"
                                value={otp}
                                onChange={handleOtpChange}
                                className="w-full py-3 px-4 text-sm border-2 border-[#EAEBF0] rounded-[10px] bg-[#F1F1F1] focus:outline-none text-black focus:ring-2 focus:border-transparent"
                                placeholder="Enter 6-digit OTP"
                                maxLength={6}
                                required
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <p className="text-red-500 text-xs text-center">{error}</p>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-3 bg-[#317333] text-white font-medium rounded-[10px] hover:bg-[#317333]/80 focus:outline-none focus:ring-2 focus:ring-[#317333] flex items-center justify-center ${loading ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            aria-busy={loading}
                            aria-label={loading ? "Processing PIN reset" : "Reset PIN"}
                        >
                            {loading ? (
                                <img src={blackload} alt="Loading" className="w-6 h-6" />
                            ) : (
                                "Reset PIN"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ForgotPin;
